import { __awaiter } from "tslib";
import { useAemConfig } from '@as-react/common-components';
import { authQueries } from '@as-react/common-queries';
import { queryClient } from '@as-react/common-queries';
import { AUTH_STORAGE_KEY, AUTH_TRIGGER_POST_LOGIN, EventEnum } from '@as-react/common-types';
import { EventUtil, getHomepageUrl, localStorageUtil } from '@as-react/common-utils';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// @TODO: Remove after new auth deploy
import { basketActions } from '../redux/basket/actions/BasketActions.creators';
import sessionActions from '../redux/session/actions/SessionActions.creators';
import { makeGetSessionState } from '../redux/session/selectors/SessionSelector';
import { isLoggedInUser } from '../util/SessionUtil';
import { generatePathUrl } from '../util/URLParamUtil';
import { useFeatureToggle } from './useFeatureToggle';
import { useWishlist } from './useWishlist';
const AUTH_LOGIN_ENDPOINT = '/auth/login';
export const useOAuth = ({ returnTo, flow, cancelUrl } = {}) => {
    const { servicesEndpoint, defaultRequestParams, rootWithLanguageSuffix, oauth, canonicalUrl, pageUrls, root, translationLang, } = useAemConfig();
    const currentUrl = typeof window !== 'undefined' && window.location ? window.location.href : canonicalUrl;
    const currentPath = typeof window !== 'undefined' && window.location ? window.location.pathname + window.location.search : '';
    const redirectUri = new URL(`${rootWithLanguageSuffix}/auth.html`, currentUrl);
    const { wishlistLogout, wishlistLogin } = useWishlist();
    const dispatch = useDispatch();
    const sessionState = useSelector(makeGetSessionState());
    const authStorage = localStorageUtil.getWithExpiry(AUTH_STORAGE_KEY);
    const isNewAuth = useFeatureToggle('use_new_auth_flow');
    const { getUserinfo, logout: logoutQuery } = authQueries({ servicesEndpoint, defaultRequestParams });
    const { data: userInfo, isEnabled, isFetched, isSuccess: isLoggedIn, } = useQuery(getUserinfo({
        enabled: isNewAuth && !!authStorage,
    }));
    const loginState = useMemo(() => {
        var _a;
        if ((isEnabled && !isFetched) || (((_a = sessionState === null || sessionState === void 0 ? void 0 : sessionState.authorities) === null || _a === void 0 ? void 0 : _a.length) === 0 && !isNewAuth)) {
            return 'PENDING';
        }
        if (isLoggedIn || isLoggedInUser(sessionState)) {
            return 'AUTHENTICATED';
        }
        return 'NOT_AUTHENTICATED';
    }, [isEnabled, isFetched, isLoggedIn, sessionState, isNewAuth]);
    const authUrl = useMemo(() => {
        const authUrl = new URL(`${AUTH_LOGIN_ENDPOINT}`, currentUrl);
        const params = new URLSearchParams(authUrl.search);
        params.append('response_type', 'code');
        params.append('client_id', oauth.clientId);
        params.append('scope', 'openid');
        params.append('redirect_uri', redirectUri.toString());
        params.append('returnTo', returnTo || currentPath);
        params.append('finishRegistration', pageUrls.finishRegistration || currentPath);
        params.append('cancelUrl', cancelUrl || currentPath);
        params.append('translationLang', translationLang);
        if (flow) {
            params.append('flow', flow);
        }
        for (const [key, value] of Object.entries(defaultRequestParams)) {
            if (value !== undefined && value !== null) {
                params.append(key, value.toString());
            }
        }
        authUrl.search = params.toString();
        return authUrl.toString();
    }, [returnTo, flow, cancelUrl, translationLang]);
    const logout = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!isNewAuth) {
            dispatch(sessionActions.createLogoutAction());
            return;
        }
        try {
            const { id_token } = localStorageUtil.getWithExpiry(AUTH_STORAGE_KEY);
            yield queryClient.fetchQuery(logoutQuery({
                clientId: oauth.clientId,
                redirectUri: currentUrl,
                oidcToken: id_token,
            }, {
                staleTime: 0,
            }));
            yield wishlistLogout();
        }
        finally {
            localStorage.removeItem(AUTH_STORAGE_KEY);
            window.location.href = getHomepageUrl(root);
        }
    });
    const postLogin = useCallback(() => {
        if (sessionStorage.getItem(AUTH_TRIGGER_POST_LOGIN) === 'true') {
            sessionStorage.removeItem(AUTH_TRIGGER_POST_LOGIN);
            wishlistLogin();
            dispatch(basketActions.createGetBasicAction());
            if (!isNewAuth) {
                EventUtil.dispatch(EventEnum.USER_LOGGED_IN, { loggedIn: true });
            }
        }
    }, [wishlistLogin, dispatch]);
    const redirectToLogin = useCallback(() => {
        const redirectUrl = isNewAuth ? authUrl : generatePathUrl(root, 'account.html').pathname;
        window.location.href = redirectUrl;
    }, [authUrl, isNewAuth]);
    return {
        authUrl,
        loginState,
        isLoggedIn: loginState === 'AUTHENTICATED',
        userInfo,
        logout,
        postLogin,
        redirectToLogin,
    };
};
